import axios from 'axios';

import { httpClient } from '@/lib/httpClient';

export interface UploadedImageFile {
  key: string;
  url: string; // public CDN URL
}

export interface PresignRequestFile {
  fileName: string;
  fileSize: number;
}

export interface PresignedFile {
  key: string;
  uploadUrl: string;
  publicUrl: string;
  contentType: string;
}

class ProductImagesService {
  async presign(files: File[]) {
    const CHUNK = 100;
    const all: PresignedFile[] = [];
    for (let start = 0; start < files.length; start += CHUNK) {
      const slice = files.slice(start, start + CHUNK);
      const body = {
        files: slice.map((f) => ({ fileName: f.name, fileSize: f.size })),
      } as { files: PresignRequestFile[] };
      const { data } = await httpClient.post<{ files: PresignedFile[] }>(
        '/v1/product-images/presign',
        body,
      );
      all.push(...data.files);
    }
    return all;
  }

  async upload(
    files: File[],
    opts?: { concurrency?: number; onProgress?: (percent: number) => void },
  ) {
    // Use presign flow for scalability; it also works for few files
    const presigned = await this.presign(files);

    const totalBytes = files.reduce((sum, f) => sum + f.size, 0) || 1;
    const loadedByIndex = new Array(files.length).fill(0);

    const onProgress = opts?.onProgress;

    const updateOverall = (idx: number, loaded: number) => {
      loadedByIndex[idx] = loaded;
      if (!onProgress) return;
      const loadedTotal = loadedByIndex.reduce((s, v) => s + v, 0);
      const pct = Math.min(100, Math.round((loadedTotal / totalBytes) * 100));
      onProgress(pct);
    };

    const results: UploadedImageFile[] = [];
    const concurrency = Math.max(1, Math.min(opts?.concurrency ?? 6, 10));
    let i = 0;

    async function worker() {
      while (i < files.length) {
        const idx = i++;
        const file = files[idx];
        const p = presigned[idx];

        await axios.put(p.uploadUrl, file, {
          headers: {
            'Content-Type': p.contentType,
            'x-amz-acl': 'public-read',
          },
          onUploadProgress: (e) => {
            const loaded = e.loaded ?? 0;
            updateOverall(idx, loaded);
          },
        });

        results.push({ key: p.key, url: p.publicUrl });
      }
    }

    await Promise.all(Array.from({ length: concurrency }, worker));

    // Ensure final progress is 100
    onProgress?.(100);

    return results;
  }
}

export default new ProductImagesService();
